import { AppColors } from "@/assets/colors";
import { AppLinks, AppSize, pxToRem } from "@/util";
import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { makeStyles } from "tss-react/mui";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import useToastAlert from "@/util/hooks/usesnackbar";

import { subscribeToNewsletter } from "@/util/api/subscribe";

export default function Footer() {
  const { classes } = styles();
  const toastAlert = useToastAlert();

  const quickLinks = [
    { text: "Home", href: AppLinks.PUBLIC.HOME },
    { text: "Portfolio", href: AppLinks.PUBLIC.PORTFOLIOS },
    { text: "Services", href: AppLinks.PUBLIC.SERVICES },
    { text: "About Us", href: AppLinks.PUBLIC.ABOUT },
    { text: "Contact Us", href: AppLinks.PUBLIC.CONTACT },
  ];

  const resourcesLinks = [
    { text: "Blog", href: AppLinks.PUBLIC.BLOG },
    { text: "FAQs", href: AppLinks.PUBLIC.ABOUT },
    { text: "Listings", href: AppLinks.PUBLIC.LISTINGS },
    { text: "Dashboard", href: AppLinks.PUBLIC.DASHBOARD },
    { text: "Privacy Policy", href: AppLinks.PUBLIC.PRIVACY },
  ];

  const [email, setEmail] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: () => subscribeToNewsletter(email),
    onSuccess: () => {
      toastAlert({ message: "Subscribed successfully!", type: "success" });
      setEmail("");
    },
    onError: () => {
      toastAlert({
        message: "Subscription failed. Please try again.",
        type: "error",
      });
    },
  });

  const handleSubscribe = () => {
    if (!email.trim()) {
      toastAlert({ message: "Please enter a valid email.", type: "warning" });
      return;
    }
    mutate();
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.sub}>
        <Box className={classes.logoCont}>
          <Image
            alt="logo"
            src={"/images/footer/logo_light.svg"}
            width={72}
            height={100.34}
          />
          <Typography className={classes.logoTxt}>
            AREL delivers premium real estate solutions, from property listings
            to expert investments and consultations.
          </Typography>
        </Box>
        <Box className={classes.links}>
          <Box className={classes.linksDiv}>
            <Box className={classes.linkHeight}>
              <Typography className={classes.linkHd}>Quick Links</Typography>
              <Box className={classes.linksCont}>
                {quickLinks.map((link, key) => (
                  <Link key={key} href={link.href} passHref>
                    <Typography className={classes.logoTxt}>
                      {link.text}
                    </Typography>
                  </Link>
                ))}
              </Box>
            </Box>
            <Box className={classes.linkHeight}>
              <Typography className={classes.linkHd}>Resources</Typography>
              <Box className={classes.linksCont}>
                {resourcesLinks.map((link, key) => (
                  <Link key={key} href={link.href} passHref>
                    <Typography className={classes.logoTxt}>
                      {link.text}
                    </Typography>
                  </Link>
                ))}
              </Box>
            </Box>
          </Box>
          <Box className={`${classes.linkHeight} ${classes.emailCont}`}>
            <Typography className={classes.linkHd}>
              Get Email Resources
            </Typography>
            <Typography className={classes.emailTxt}>
              Sign up to our e-mail newsletter to receive the latest news &
              updates.
            </Typography>
            <Box className={classes.submitCont}>
              <TextField
                placeholder="Input your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                className={classes.emailInput}
                sx={{ input: textFieldStyleInput }}
              />
              <Button
                onClick={handleSubscribe}
                disabled={isPending}
                className={classes.submitBtn}
                variant="contained"
                loading={isPending}
              >
                {isPending ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box>
        <Divider color={AppColors.secondary[200]} className={classes.divider} />
        <Box className={classes.socialCont}>
          <Typography className={classes.allRightTxt}>
            © {new Date().getFullYear().toString()} <a href="https://avvicgroup.com/tech" target="_blank">AvvicTech</a>. All Rights Reserved.
          </Typography>
          <Box className={classes.socialIcons}>
            <IconButton>
              <FacebookRoundedIcon className={classes.socialIcon} />
            </IconButton>
            <IconButton>
              <Link href="https://wa.me/2349063157836" target="_blank">
                <WhatsAppIcon className={classes.socialIcon} />
              </Link>
            </IconButton>
            <IconButton>
              <Link
                href="https://www.instagram.com/arel_homes/?igsh=Yzkwc2E4Y3NkbjN3&utm_source=qr#"
                target="_blank"
              >
                <InstagramIcon className={classes.socialIcon} />
              </Link>
            </IconButton>
            {/*  */}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const textFieldStyleInput = {
  color: AppColors.text.second,
  height: "100%",
};

const styles = makeStyles()((theme) => ({
  container: {
    minHeight: pxToRem(350),
    backgroundColor: theme.colors.secondary,
    display: "grid",
    gridTemplateRows: "3fr 1fr",
    padding: `0px ${pxToRem(AppSize.padding)}`,
    [theme.breakpoints.down("sm")]: {
      padding: `${pxToRem(20)}`,
    },
  },
  sub: {
    display: "grid",
    gridTemplateColumns: "1.5fr 3fr",
    alignItems: "flex-start",
    gap: theme.spacing(4),
    paddingTop: theme.spacing(4),
    //paddingBottom: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  logoCont: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    justifyContent: "center",
  },
  logoTxt: {
    fontWeight: 500,
    fontSize: pxToRem(16),
    color: AppColors.neutral["60"],
    textAlign: "justify",
    width: pxToRem(247),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  emailTxt: {
    fontWeight: 400,
    fontSize: pxToRem(16),
    color: AppColors.neutral["60"],
    textAlign: "justify",
    width: pxToRem(247),
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  links: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(5),
    },
  },
  linkHd: {
    fontWeight: 600,
    fontSize: pxToRem(16),
    color: theme.colors.white,
    [theme.breakpoints.down("sm")]: {
      width: "150%",
    },
  },
  linksCont: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(3),
      width: "133%",
    },
  },
  linkHeight: {
    height: "70%",
  },
  emailCont: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  submitCont: {
    display: "flex",
    flexDirection: "row",
    height: pxToRem(50),
  },
  emailInput: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      height: "100%",
      borderTopLeftRadius: pxToRem(8),
      borderBottomLeftRadius: pxToRem(8),
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      "& fieldset": {
        borderColor: AppColors.secondary[200],
      },
      "&:hover fieldset": {
        borderColor: AppColors.primary[500],
      },
      "&.Mui-focused fieldset": {
        borderColor: AppColors.primary[400],
      },
      "&.Mui-disabled fieldset": {
        borderColor: AppColors.neutral[30],
      },
    },
  },
  submitBtn: {
    width: pxToRem(95),
    height: pxToRem(50),
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderTopRightRadius: pxToRem(8),
    borderBottomRightRadius: pxToRem(8),
  },
  socialCont: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(2),
      alignItems: "flex-start",
    },
  },
  socialIcons: {
    display: "flex",
    gap: theme.spacing(2),
  },
  socialIcon: {
    width: pxToRem(24),
    height: pxToRem(24),
    color: theme.colors.white,
  },
  allRightTxt: {
    fontWeight: 500,
    fontSize: pxToRem(16),
    color: AppColors.neutral["60"],
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },
  linksDiv: {
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "baseline",
      gap: pxToRem(190),
    },
    display: "flex",
  },
  divider: {
    [theme.breakpoints.down("sm")]: {
      marginTop: pxToRem(60),
    },
  },
}));
